import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export type StatSource = 'meta' | 'momence' | 'stripe' | 'derived' | 'lead' | 'booking';
export type StatStatus = 'good' | 'warn' | 'bad';

const SOURCE_DOT: Record<StatSource, string> = {
  meta: 'bg-accent-click',
  momence: 'bg-accent-booking',
  stripe: 'bg-accent-money',
  derived: 'bg-fg-subtle',
  lead: 'bg-accent-lead',
  booking: 'bg-accent-booking',
};

const STATUS_TEXT: Record<StatStatus, string> = {
  good: 'text-good-fg',
  warn: 'text-warn-fg',
  bad: 'text-bad-fg',
};

interface StatCardProps {
  label: ReactNode;
  value: ReactNode;
  source?: StatSource;
  sub?: ReactNode;
  status?: StatStatus;
}

/**
 * Canonical headline metric. Label (+ source dot) · value · optional sub.
 * Status tints only the value, never the whole card (color = verdict).
 */
export function StatCard({ label, value, source, sub, status }: StatCardProps) {
  return (
    <div className="min-w-0 rounded-lg border border-border bg-surface p-3 shadow-card sm:p-4">
      <div className="flex items-center gap-1.5">
        {source ? (
          <span className={cn('size-1.5 shrink-0 rounded-full', SOURCE_DOT[source])} />
        ) : null}
        <span className="truncate text-label text-fg-muted">{label}</span>
      </div>
      <div
        className={cn('mt-2 truncate text-title tabular', status ? STATUS_TEXT[status] : 'text-fg')}
      >
        {value}
      </div>
      <div className="mt-1 min-h-4 text-label text-fg-subtle">{sub}</div>
    </div>
  );
}
